import { RemoteTrack, Track } from 'livekit-client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { incrementVideoCount, decrementVideoCount, setPinnedTrackId } from '../../../store/livekitSlice/livekitSlice';
import { useTracker } from '../../../../../utils/visibilityTracker';
import { useFloating, shift, flip, offset, autoUpdate } from "@floating-ui/react";
import HandRankBadge from "../HandRankBadge/HandRankBadge";
import { useTranslation } from 'react-i18next';
import ActionConfirmation from '../../../../../components/ActionConfirmation';
import { createPortal } from "react-dom";
import MediaContainer from '../MediaContainer/MediaContainer';

/**
 * RemoteVideo component renders a remote video track from a participant.
 * @param {RemoteTrack} remoteVideoTrack - The remote video track to display.
 * @param {Object} participantInfo - Information about the participant (includes metadata).
 * @returns {JSX.Element}
 */
export default function RemoteVideo({
  remoteVideoTrack,
  participantInfo,
  participantAudioTrack,
  autoZoom = false,
  handRank,
  handleStopCamera,
  cameraDisabledLocal,
  handleCloseMicClick,
  handleConfirmCloseMic,
  confirmDialogMiCOpen,
  setconfirmDialogMicOpen,
  handleFireUserClick,
  handleConfirmFireUser,
  setConfirmFireUserDialogOpen,
  ConfirmFireUserDialogOpen,
  handleInviteToGame,
  type,
  forcedName
}) {
  const videoRef = useRef(null);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [rerenderTrigger, setRerenderTrigger] = useState(true);
  const [open, setOpen] = useState(false);
  const [floatingVideo, setFloatingVideo] = useState(false);

  const [isRemotePortrait, setIsRemotePortrait] = useState(false);

  const isMobile = window.innerWidth < 480;
  const { refs, floatingStyles } = useFloating({
    placement: "bottom-end",
    middleware: [
      offset(6),
      flip(),
      shift(),
    ],
    whileElementsMounted: autoUpdate,
  });

  const { zoomedTrackId, conferenceMode, pinnedTrackId } = useSelector((state) => state.livekit);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const pinnedTrackIdRef = useRef(pinnedTrackId);
  const { localAvatar } = useSelector((state) => state.auth);

  // Parse participant metadata
  let participantMetadata = {};
  try {
    participantMetadata = JSON.parse(participantInfo?.metadata || "{}");
  } catch (error) {
    console.error("Error parsing participant metadata:", error);
  }

  // Increment video count on mount, decrement on unmount
  useEffect(() => {
    pinnedTrackIdRef.current = pinnedTrackId;
  }, [pinnedTrackId]);

  // Increment video count on mount, decrement on unmount
  useEffect(() => {
    console.log('RemoteVideo mounted:', remoteVideoTrack.sid);

    dispatch(incrementVideoCount());
    return () => {
      console.log('RemoteVideo unmounted:', remoteVideoTrack.sid, pinnedTrackIdRef?.current, pinnedTrackIdRef.current == remoteVideoTrack.sid);
      if (pinnedTrackIdRef?.current == remoteVideoTrack.sid) {
        dispatch(setPinnedTrackId(null));
      }

      dispatch(decrementVideoCount());
    };
  }, [dispatch, remoteVideoTrack.sid]);

  // Handle pin track
  const handlePinTrack = useCallback(() => {
    dispatch(setPinnedTrackId(remoteVideoTrack.sid));
    setOpen(false);
  }, [dispatch, remoteVideoTrack.sid]);

  // Attach video track using LiveKit's attach() method for adaptive streaming
  useEffect(() => {
    const videoElement = videoRef.current;

    if (!remoteVideoTrack || !videoElement) {
      setIsVideoLoading(false);
      return;
    }

    setIsVideoLoading(true);

    const onVideoReady = () => {
      console.log(`Video ready for track: ${remoteVideoTrack.sid}`);

      videoElement.play().catch(error => {
        if (error.name !== 'AbortError') {
          console.error("Failed to play video:", error);
        }
      });

      setIsVideoLoading(false);
      videoElement.removeEventListener("loadeddata", onVideoReady);
    };

    videoElement.addEventListener("loadeddata", onVideoReady);

    try {
      remoteVideoTrack.attach(videoElement);

      if (videoElement.readyState >= 2) {
        onVideoReady();
      }
    } catch (error) {
      console.error("Error attaching video track:", error);
      setIsVideoLoading(false);
    }

    return () => {
      videoElement.removeEventListener("loadeddata", onVideoReady);
    };

  }, [remoteVideoTrack]);

  // Track video visibility using unified visibility tracker
  const visibilityTrackerRef = useTracker("video-" + remoteVideoTrack.sid);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
  
    const updateOrientation = () => {
      const w = el.videoWidth;
      const h = el.videoHeight;
      if (!w || !h) return;
      setIsRemotePortrait(h > w);
    };
  
    el.addEventListener("loadedmetadata", updateOrientation);
    el.addEventListener("resize", updateOrientation); // utile sur certains navigateurs
  
    // si déjà prêt
    updateOrientation();
  
    return () => {
      el.removeEventListener("loadedmetadata", updateOrientation);
      el.removeEventListener("resize", updateOrientation);
    };
  }, []);


  useEffect(() => {
    const videoElement = videoRef.current;

    if (!videoElement) {
      return;
    }

    const handleVisibilityChange = (isVisible, details) => {
      if (isVisible) {
        videoElement.play().catch(error => {
          if (error.name !== 'AbortError') {
            console.error("Failed to play video on visibility change:", error);
          }
        });
        setRerenderTrigger(prev => !prev);
      }
    };

    const cleanup = visibilityTrackerRef.current.track(videoElement, handleVisibilityChange);
    return cleanup;
  }, [remoteVideoTrack.sid, visibilityTrackerRef]);

  // Determine if this is a screen share track
  const isScreenShare = remoteVideoTrack.source === Track.Source.ScreenShare;
  const isZoomed = zoomedTrackId === remoteVideoTrack.sid;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !refs.reference.current?.contains(e.target) &&
        !refs.floating.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [refs.reference, refs.floating]);

  return (
    <>
      <MediaContainer
        mediaId={remoteVideoTrack.sid}
        canZoom={true}
        autoZoom={autoZoom}
        canFullscreen={isScreenShare} // Parent decides: only screen shares can fullscreen
        canFloat={floatingVideo} // Parent controls floating state
        containerType="video"
        className={`${rerenderTrigger ? 'rerender-trigger' : ''}`}
      >
        {!isScreenShare &&
          <span className={`mic-status ${participantAudioTrack ? 'mic-active' : 'mic-inactive'}`}>
            {participantAudioTrack ?
              <span className="material-symbols-outlined">
                mic
              </span>
              :
              <span className="material-symbols-outlined">
                mic_off
              </span>
            }
          </span>
        }

        {(type === 'remoteVideo' || (type === 'remoteScreenShare' && !isZoomed)) &&
          <div className={`video-control-panel ${open ? 'visible' : ''}`}>
            <div className='btn-video-control-panel menu-video-control-panel' ref={refs.setReference} onClick={(e) => {
              e.stopPropagation();
              setOpen(prev => !prev);
            }}>
              <span className="material-symbols-outlined">
                more_vert
              </span>
            </div>
          </div>
        }

        <video
          ref={videoRef}
          autoPlay
          playsInline
          className={`video-track ${isRemotePortrait ? "remote-portrait" : "remote-landscape"}`}
          muted={false}
          // className='video-track'
          style={{ display: 'block' }}
        />
        <HandRankBadge rank={handRank} hide={isScreenShare} />
        {isVideoLoading && (
          <div className="video-loading-spinner"></div>
        )}

        <div
          className={`video-track-label
    ${isScreenShare ? 'video-track-label-with-icon' : ''}
    ${isScreenShare && conferenceMode && isZoomed ? 'video-track-label-with-icon-conference-mode' : ''}
  `}
        >
          {isScreenShare ? (
            <>
              <span className="material-symbols-outlined">computer</span>
           {participantMetadata?.name}

            </>
          ) : (
            <span className="video-track-text" title={participantMetadata?.name}>
              { forcedName ||  participantMetadata?.name  || "Inconnu"} 
            </span>)}
        </div>
      </MediaContainer>

      {open &&
        createPortal(
          <div className='actions-menu-video' ref={refs.setFloating} style={
            isMobile
              ? { position: 'absolute', left: 0, right: 0, bottom: 0 }
              : floatingStyles
          }>
            <ul>
              {participantAudioTrack && type !== 'remoteScreenShare' &&
                <li className='action-menu-item' onClick={handleCloseMicClick}>
                  <span className="material-symbols-outlined">
                    mic_off
                  </span>
                  Couper le micro
                </li>
              }
              {!cameraDisabledLocal && type !== 'remoteScreenShare' &&
                <li className='action-menu-item' onClick={handleStopCamera}>
                  <span className="material-symbols-outlined">
                    videocam_off
                  </span>
                  Ne pas afficher la caméra
                </li>
              }
              <li className='action-menu-item' onClick={handlePinTrack}>
                {pinnedTrackId !== remoteVideoTrack.sid ?
                  <>
                    <span className="material-symbols-outlined">
                      keep_public
                    </span>
                    Epingler
                  </>
                  :
                  <>
                    <span className="material-symbols-outlined">
                      keep_off
                    </span>
                    Désépingler
                  </>
                }
              </li>
              {(type !== 'remoteScreenShare' && localAvatar?.is_guest === 0) &&
                <li className='action-menu-item' onClick={handleFireUserClick}>
                  <span className="material-symbols-outlined">
                    do_not_disturb_on
                  </span>
                  Exclusion
                </li>
              }

              {isMobile &&
                <li className='action-menu-item' onClick={() => {
                  setFloatingVideo(prev => !prev);
                  setOpen(prev => !prev);
                }}>
                  <span className="material-symbols-outlined">
                    drag_handle
                  </span>
                  {floatingVideo ? t("floating_mode_off") : t("floating_mode_on")}
                </li>
              }
              {type !== 'remoteScreenShare' &&
                <li className='action-menu-item' onClick={() => {
                  setOpen(false);
                  handleInviteToGame('TICTACTOE');
                }}>
                  <span className="material-symbols-outlined">
                    sports_esports
                  </span>
                  Inviter au Tic-Tac-Toe
                </li>
              }
            </ul>
          </div>,
          document.body
        )
      }

      <ActionConfirmation
        open={confirmDialogMiCOpen}
        title={t("close_mic_header")}
        msg={t("couper_Micor")}
        cancelText={t("cancel")}
        confirmText={t("confirm")}
        icon="mic_off"
        onClose={() => setconfirmDialogMicOpen(false)}
        onConfirm={handleConfirmCloseMic}
      />
      <ActionConfirmation
        open={ConfirmFireUserDialogOpen}
        onClose={() => setConfirmFireUserDialogOpen(false)}
        onConfirm={handleConfirmFireUser}
        title={t("virer_utilisateur_header")}
        msg={t("virer_utilisateur", { name: participantMetadata?.name })}
        variant="destructive"
        cancelText={t("cancel")}
        confirmText={t("confirm")}
      />
    </>
  );
};
